import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { USER_PROFILES, UserProfile } from '../../../shared/constants';

/**
 * Serviço de gerenciamento de perfis de usuário
 * 
 * Replica a lógica do AngularJS (application-context.service.js)
 * Gerencia perfis: PROFESSOR, SECRETARIO, ADMIN
 */
@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private profileSubject = new BehaviorSubject<UserProfile | null>(null);
  private hasServidorToSelectSubject = new BehaviorSubject<boolean>(false);

  public profile$: Observable<UserProfile | null> = this.profileSubject.asObservable();
  public hasServidorToSelect$: Observable<boolean> = this.hasServidorToSelectSubject.asObservable();

  getProfile(): UserProfile | null {
    return this.profileSubject.value;
  }

  setProfile(profile: UserProfile | null): void {
    this.profileSubject.next(profile);
  }

  isProfileAdmin(): boolean {
    const profile = this.getProfile();
    return profile === USER_PROFILES.ADMIN;
  }

  isProfileProfessor(): boolean {
    const profile = this.getProfile();
    return profile === USER_PROFILES.PROFESSOR;
  }

  isProfileSecretario(): boolean {
    const profile = this.getProfile();
    return profile === USER_PROFILES.SECRETARIO;
  }

  /** True para PROFESSOR ou SECRETARIO */
  profileHasProfessor(): boolean {
    return this.isProfileProfessor() || this.isProfileSecretario();
  }

  setAsAdmin(hasServidor: boolean): void {
    this.setHasServidorToSelect(hasServidor);
    this.setProfile(USER_PROFILES.ADMIN);
  }

  setAsSecretario(): void {
    this.setHasServidorToSelect(true);
    this.setProfile(USER_PROFILES.SECRETARIO);
  }

  setAsProfessor(): void {
    this.setProfile(USER_PROFILES.PROFESSOR);
  }

  /** Replica getProfileDefault() do AngularJS */
  getProfileDefault(servidor: { id?: number | null } | null): UserProfile | null {
    const hasServidorVinculado = servidor?.id != null && servidor.id > 0;
    
    if (hasServidorVinculado) {
      return USER_PROFILES.PROFESSOR;
    }
    
    return null;
  }

  setHasServidorToSelect(hasServidor: boolean): void {
    this.hasServidorToSelectSubject.next(hasServidor);
  }

  getHasServidorToSelect(): boolean {
    return this.hasServidorToSelectSubject.value;
  }

  clearProfile(): void {
    this.profileSubject.next(null);
    this.hasServidorToSelectSubject.next(false);
  }

  getProfileInfo(): { profile: UserProfile | null; hasServidorToSelect: boolean } {
    return {
      profile: this.getProfile(),
      hasServidorToSelect: this.getHasServidorToSelect()
    };
  }

  /**
   * Replica isValidServidor() do AngularJS:
   * PROFESSOR sempre válido, SECRETARIO só se selecionou servidor, ADMIN não precisa.
   */
  isValidServidor(servidorId?: number | null): boolean {
    if (this.isProfileProfessor()) {
      return true;
    }

    if (this.isProfileSecretario()) {
      return servidorId != null && servidorId > 0;
    }

    if (this.isProfileAdmin()) {
      return true;
    }

    return false;
  }

  /** Replica nextEntidadeState() do AngularJS: 'ano-letivo' se PROFESSOR, senão 'servidor' */
  getNextStepAfterEntidade(): 'servidor' | 'ano-letivo' {
    if (this.isProfileProfessor()) {
      return 'ano-letivo';
    }
    return 'servidor';
  }
}
